'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { CITY_EDITIONS, type CityCode, type CityEdition } from '@loci/city-editions';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/primitives/popover';
import { cn } from '@/components/atoms/primitives/utils';
import {
  GLOBAL_NAV_ACCENT_VARS,
  type GlobalNavLayer,
} from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import { useCityIsStaging } from '@/lib/city-context.client';
import {
  apexUrl,
  cityAppEntryUrl,
  cityUrl,
  defaultCityCode,
  type CrossHostStageOptions,
} from '@/lib/cross-host-urls';

/**
 * WayfinderSheet — the layer-switcher continuity device (WEB-333 → WEB-616 →
 * FRE-1361, DS / PL-08 / DES-85).
 *
 * The trigger renders whatever `children` the caller composes; the default
 * (no `children`) is a compact labeled trigger (`SiteHeader`, `AppHeader`
 * still compose a full lockup + chevron there, per WEB-616). On the city
 * surface (`CityHeader`), FRE-1361 reverses WEB-616: the brand lockup is its
 * own home link and this trigger is instead a standalone 22px round
 * "Wayfinder switch" icon-button beside it — pass a bare icon as `children`
 * and drop the default `min-h-11` hit-target sizing via `className`
 * (`min-h-0 min-w-0`) for that compact case. The accessible name still comes
 * from {@link defaultTriggerAriaLabel} either way.
 *
 * Presentation is viewport-responsive from one content tree:
 *  - below `lg`: fixed inset card under the chrome (`top: 88px`, 12px sides)
 *  - `lg`+: 420px popover anchored under the trigger
 *
 * Built on the Radix Popover primitive: Escape closes, outside click closes,
 * focus returns to the trigger, and `aria-expanded` is stamped on the button.
 */

export type WayfinderLayer = GlobalNavLayer;

/** Lifted city-name accent when the Wayfinder trigger is open (PL-08).
 * City/apex stay on the bright lift (dark chrome). App uses `--nav-accent`
 * so the open chip clears WCAG AA on light chrome (WEB-638 / DES-62 PL-16). */
export const WAYFINDER_CITY_LABEL_OPEN = '#7FD9D9';

function wayfinderCityOpenToken(layer: WayfinderLayer): string {
  return layer === 'app' ? 'var(--nav-accent)' : WAYFINDER_CITY_LABEL_OPEN;
}

export interface WayfinderSheetProps {
  /** Which global-nav layer the host header lives on — marked `NOW` in the sheet. */
  layer: WayfinderLayer;
  /** City code used for the city/app layer hrefs. Defaults to the registry default. */
  cityCode?: CityCode;
  /** City display name for the city-layer sub line + city chip. Defaults from the registry. */
  cityName?: string;
  /**
   * Custom trigger contents (WEB-616). When provided, wraps `children` in the
   * composed ≥44px trigger button (city lockup). When omitted, renders the
   * compact labeled default used by SiteHeader / AppHeader.
   */
  children?: React.ReactNode;
  /**
   * Accessible name for the trigger. Defaults to
   * `"LOCI {city} — switch layer or city"` when `cityName` is known, otherwise
   * `"Switch layer or city (Wayfinder)"`.
   */
  triggerAriaLabel?: string;
  /** Initial open state — Storybook / tests only. */
  defaultOpen?: boolean;
  /** Extra classes on the trigger button. */
  className?: string;
  /** Inline styles on the trigger button (e.g. open-state accent token). */
  style?: React.CSSProperties;
}

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

// The registry only ships live editions (CLT for the MVP) while `CityCode`
// is the wider union — index through a partial record so lookups stay safe.
export const CITY_EDITION_LOOKUP: Partial<Record<CityCode, CityEdition>> = CITY_EDITIONS;

/** `https://app.loci.city/clt` → `app.loci.city/clt` (display label for a helper-built URL). */
function hostLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/$/, '');
    return `${parsed.host}${path}`;
  } catch {
    return url;
  }
}

export interface WayfinderLayerHref {
  readonly key: WayfinderLayer;
  readonly href: string;
  readonly sub: string;
}

interface LayerRow extends WayfinderLayerHref {
  readonly glyph: string;
}

const LAYER_GLYPHS: Record<WayfinderLayer, string> = {
  apex: '◯',
  city: '◐',
  app: '●',
};

function defaultTriggerAriaLabel(cityName: string | undefined): string {
  if (cityName) {
    return `LOCI ${cityName}, switch layer or city`;
  }
  return 'Switch layer or city (Wayfinder)';
}

/**
 * Shared apex / city / app layer hrefs + sub-copy for Wayfinder and surface
 * menus (WEB-628). Keep one source of truth so AppMenuSheetContent cannot
 * drift from WayfinderSheet.
 */
export function buildWayfinderLayerHrefs(
  cityCode: CityCode,
  cityName: string,
  stage?: CrossHostStageOptions,
): readonly WayfinderLayerHref[] {
  return [
    { key: 'apex', href: apexUrl('/', stage), sub: 'Company · global' },
    {
      key: 'city',
      href: cityUrl(cityCode, '/', stage),
      sub: `Public discovery · ${cityName}`,
    },
    {
      key: 'app',
      href: cityAppEntryUrl(cityCode, stage),
      sub: 'Your app · saved & tickets',
    },
  ];
}

export function WayfinderSheet({
  layer,
  cityCode = defaultCityCode(),
  cityName,
  children,
  triggerAriaLabel,
  defaultOpen = false,
  className,
  style,
}: WayfinderSheetProps): React.ReactElement {
  const isStaging = useCityIsStaging();
  const resolvedCityName = cityName ?? CITY_EDITION_LOOKUP[cityCode]?.displayName ?? cityCode;
  // Composed lockup (children) uses the PL-08 name when cityName is known.
  // Compact inherited triggers keep the legacy label so SiteHeader / AppHeader
  // axe + RTL contracts stay stable.
  const ariaLabel =
    triggerAriaLabel ??
    (children
      ? defaultTriggerAriaLabel(cityName ?? resolvedCityName)
      : 'Switch layer or city (Wayfinder)');

  const rows: readonly LayerRow[] = React.useMemo(
    () =>
      buildWayfinderLayerHrefs(cityCode, resolvedCityName, { isStaging }).map((row) => ({
        ...row,
        glyph: LAYER_GLYPHS[row.key],
      })),
    [cityCode, resolvedCityName, isStaging],
  );

  return (
    <Popover defaultOpen={defaultOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          data-testid="wayfinder-trigger"
          style={
            {
              '--wayfinder-city-open': wayfinderCityOpenToken(layer),
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            'group inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-ds-md',
            'transition-colors duration-150 motion-reduce:transition-none',
            FOCUS_RING,
            className,
          )}
        >
          {children ?? (
            <>
              <span
                aria-hidden="true"
                className={cn(
                  'inline-flex items-center whitespace-nowrap font-mono text-micro uppercase tracking-[0.14em]',
                  'text-[color:var(--gn-accent-glow)]',
                  'group-hover:text-[color:var(--wayfinder-city-open,#7FD9D9)]',
                  'group-data-[state=open]:text-[color:var(--wayfinder-city-open,#7FD9D9)]',
                )}
              >
                {resolvedCityName}
              </span>
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'size-3 shrink-0 text-[color:var(--gn-accent-glow)]',
                  'group-hover:text-[color:var(--wayfinder-city-open,#7FD9D9)]',
                  'group-data-[state=open]:text-[color:var(--wayfinder-city-open,#7FD9D9)]',
                )}
                strokeWidth={2}
              />
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        avoidCollisions={false}
        aria-label="Wayfinder"
        data-testid="wayfinder-sheet"
        style={GLOBAL_NAV_ACCENT_VARS[layer]}
        className={cn(
          'rounded-ds-xl border-border-strong p-[18px] text-fg-1',
          'bg-[linear-gradient(180deg,var(--surface)_0%,var(--bg)_100%)]',
          // DS `--gn-sheet-bg` shadow: drop shadow + a 1px inner highlight hairline.
          'shadow-[0_32px_64px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.04)_inset]',
          // DS `gn-sheet-in` entrance (fade + translateY(-8px), decelerate, no
          // scale). The inherited shadcn `animate-in` utilities are inert here
          // (no tailwindcss-animate plugin), so the sheet had no entrance at all.
          // Reduced-motion is handled centrally in styles/overrides/reduced-motion.css.
          'animate-loci-sheet-in',
          // WEB-616 / PL-08 presentation:
          // < lg → fixed inset card (top 88px, 12px sides) — enforced in
          //   `styles/overrides/chrome.css` on the Radix popper wrapper (`:has` this
          //   test id), because a transform-bearing wrapper defeats
          //   `position: fixed` on the content node alone.
          // lg+  → 420px popover anchored under the lockup
          'w-[min(420px,calc(100vw-24px))]',
        )}
      >
        <div className="mb-2.5 flex items-center justify-between gap-3 font-mono text-micro uppercase tracking-[0.18em] text-[color:var(--gn-accent-glow)]">
          <span>Wayfinder</span>
          <b className="font-medium text-fg-3">You are here · {layer.toUpperCase()}</b>
        </div>

        <nav aria-label="LOCI layers" className="mb-3.5 flex flex-col gap-1">
          {rows.map((row) => {
            const isCurrent = row.key === layer;
            return (
              <a
                key={row.key}
                href={row.href}
                aria-current={isCurrent ? 'true' : undefined}
                data-testid={`wayfinder-layer-${row.key}`}
                className={cn(
                  'grid grid-cols-[28px_1fr_auto] items-center gap-3 rounded-ds-md border px-3 py-2.5 no-underline',
                  'transition-colors duration-150 motion-reduce:transition-none',
                  FOCUS_RING,
                  isCurrent
                    ? 'border-[var(--gn-accent)] bg-[var(--gn-accent-wash)]'
                    : 'border-transparent bg-white/[0.02] hover:border-glass-border hover:bg-glass-bg',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'grid size-7 place-items-center rounded-ds-md border font-mono text-micro font-semibold',
                    isCurrent
                      ? 'border-[var(--gn-accent)] bg-[var(--gn-accent)] text-clt-navy'
                      : 'border-glass-border bg-glass-bg text-fg-2',
                  )}
                >
                  {row.glyph}
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-mono text-caption tracking-[0.02em] text-fg-1">
                    {hostLabel(row.href)}
                  </span>
                  <span className="truncate text-micro text-fg-3">{row.sub}</span>
                </span>
                {isCurrent ? (
                  <span
                    data-testid="wayfinder-now-pill"
                    className="rounded-full border border-[var(--gn-accent)] bg-[var(--gn-accent-wash)] px-[7px] py-[3px] font-mono text-micro uppercase tracking-[0.08em] text-[color:var(--gn-accent-glow)]"
                  >
                    Now
                  </span>
                ) : null}
              </a>
            );
          })}
        </nav>

        <div className="mx-1 mb-1.5 mt-2 font-mono text-micro uppercase tracking-[0.16em] text-fg-3">
          Switch city
        </div>
        {/*
         * Launch readiness: the "Request a city" chip linked to the apex
         * `/contact` coming-soon stub and is hidden until a real request /
         * waitlist route exists. The live city chip remains.
         */}
        <div className="grid grid-cols-1 gap-1">
          <a
            href={cityUrl(cityCode, '/', { isStaging })}
            data-testid="wayfinder-city-chip"
            className={cn(
              'flex items-center gap-2 rounded-ds-md border border-glass-border bg-white/[0.02] px-2.5 py-2 font-display text-caption text-fg-1 no-underline',
              'transition-colors duration-150 hover:border-[var(--gn-accent)] hover:bg-[var(--gn-accent-wash)] motion-reduce:transition-none',
              FOCUS_RING,
            )}
          >
            <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-clt-skyline" />
            {resolvedCityName}
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
